import docIcon from "../assets/images/icons/docIcon.svg" // Replace with actual image paths
import pdfIcon from "../assets/images/icons/pdfIcon.svg" // Replace with actual image paths
import excelIcon from "../assets/images/icons/excelIcon.svg" // Replace with actual image paths
import constants from "./en"
import * as Yup from "yup"
import { jwtDecode } from "jwt-decode"

// Lazy import to avoid circular dependency:
// axiosInstance.js imports AuthTokenManager from this file,
// so we can't import axiosInstance at the top level.
const getAxios = () => require("../lib/axiosInstance").default

export const isSessionExpired = () => {
  const session = JSON.parse(localStorage.getItem("sessionTime"))
  if (!session) return true // No session, consider expired

  const currentTime = Date.now()
  const sessionStartTime = session.startTime
  const sessionDuration = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

  return currentTime - sessionStartTime > sessionDuration
}
export const getFileIcon = (extension) => {
  if (extension === "pdf") {
    return pdfIcon // Return the PDF icon
  } else if (extension === "doc" || extension === "docx") {
    return docIcon // Return the DOC icon
  } else if (extension === "xls" || extension === "xlsx") {
    return excelIcon // Return the Excel icon
  } else if (
    extension === "jpg" ||
    extension === "JPG" ||
    extension === "JPEG"
  ) {
    return docIcon // Return the JPG icon
  } else if (extension === "png" || extension === "PNG") {
    return docIcon // Return the PNG icon
  } else {
    return null // No icon for unsupported file types
  }
}


export const getFacilitySFList = async (facilityPrompt = "") => {
  try {
    const response = await getAxios().get(
      `/facilities?facilityPrompt=${encodeURIComponent(facilityPrompt)}&limit=20`,
    )
    return response.data
  } catch (error) {
    console.error("Error fetching data with headers:", error)
    throw error // Propagate the error
  }
}

export const getAdminSFList = async (facilityId = "") => {
  try {
    const response = await getAxios().get(
      `/salesforceUsers?facilityId=${encodeURIComponent(facilityId)}&offset=0&limit=${constants.LIMIT}`,
    )
    return response.data
  } catch (error) {
    console.error("Error fetching data with headers:", error)
    throw error // Propagate the error
  }
}

export const addReportValidationSchema = Yup.object().shape({
  rows: Yup.array().of(
    Yup.object().shape({
      report_name: Yup.mixed().required(constants.EMPTY_ERROR_MESSAGE),
      iframe_url: Yup.string()
        .required(constants.EMPTY_ERROR_MESSAGE)
        .matches(
          /^https:\/\/[^\s/$.?#].[^\s]*$/,
          "Must be a valid https:// URL",
        ),
    }),
  ),
})

export const editReportValidationSchema = Yup.object({
  report_name: Yup.mixed().required(constants.EMPTY_ERROR_MESSAGE),
  iframe_url: Yup.string()
    .required(constants.EMPTY_ERROR_MESSAGE)
    .matches(/^https:\/\/[^\s/$.?#].[^\s]*$/, "Must be a valid https:// URL"),
})

export const removeDecimal = (number) => {
  if (number === null || number === undefined || number === "-") {
    return "-" // Explicit missing-value sentinel
  }
  const n = Number(number)
  if (!Number.isFinite(n)) {
    return "-" // Catches NaN, Infinity, and non-numeric strings
  }
  return Math.trunc(n).toString()
}

let authToken = null
export const AuthTokenManager = {
  set: (token) => {
    authToken = token
  },
  get: () => {
    return authToken
  },
  decode: () => {
    return jwtDecode(authToken)
  },
  remove: () => {
    authToken = null
  },
  expired: () => {
    const decodedToken = jwtDecode(AuthTokenManager.get())
    const currentTime = Math.floor(Date.now() / 1000) // Current time in seconds
    return decodedToken.exp < currentTime
  },
}

export const formatToUSCurrency = (value) => {
  // ✅ Return "-" for null, undefined, or empty string
  if (value == null || value === "") return "-"

  // ✅ Convert strings to numbers safely
  const numericValue = typeof value === "string" ? Number(value) : value

  // ✅ Handle invalid number inputs
  if (Number.isNaN(numericValue)) return "-"

  // ✅ Format valid numbers as US currency-style decimals
  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export const customDateSort = (date1, date2) => {
  const parseDate = (dateString) => {
    if (!dateString) return new Date(0)
    const [month, day, year] = dateString.split("/")
    return new Date(year, month - 1, day)
  }

  const dateA = parseDate(date1)
  const dateB = parseDate(date2)

  if (dateA < dateB) return -1
  if (dateA > dateB) return 1
  return 0
}

// "-" for values that aren't real dates, e.g. the SF "pretty" formula
// renders "December ," when its source date is null.
export const dateOrDash = (value) =>
  value && !Number.isNaN(new Date(value).getTime()) ? value : "-"

// Draw the diagonal CONFIDENTIAL watermark on the current page.
//
// jsPDF's align:"center" combined with `angle` rotates around the wrong
// anchor and produces visibly off-center text. Compute the anchor offset
// along the rotated baseline so the text lands centered on the page.
export const drawConfidentialWatermark = (doc, options = {}) => {
  const {
    text = constants.WATERMARK_TEXT,
    angle = constants.WATERMARK_ANGLE_DEGREE,
    fontSize = 60,
    opacity = 0.1,
    color = [100, 116, 139], // slate-500
  } = options

  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()

  doc.saveGraphicsState()
  if (doc.setGState && doc.GState) {
    doc.setGState(doc.GState({ opacity }))
  }
  doc.setFont("helvetica", "bold")
  doc.setFontSize(fontSize)
  doc.setTextColor(...color)

  const angleRad = (angle * Math.PI) / 180
  const halfW = doc.getTextWidth(text) / 2
  doc.text(
    text,
    pageW / 2 - halfW * Math.cos(angleRad),
    pageH / 2 + halfW * Math.sin(angleRad),
    { angle, baseline: "middle" },
  )

  doc.restoreGraphicsState()
}

// Draw the confidentiality notice in the page footer. `y` defaults to a
// position 10mm above the bottom edge so it clears typical printer
// unprintable margins.
export const drawConfidentialFooter = (doc, options = {}) => {
  const {
    text = constants.WATERMARK_FOOTER_TEXT,
    fontSize = 8,
    color = [100, 116, 139], // slate-500
    y,
  } = options

  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()

  doc.setFont("helvetica", "normal")
  doc.setFontSize(fontSize)
  doc.setTextColor(...color)
  doc.text(text, pageW / 2, y ?? pageH - 10, {
    align: "center",
    maxWidth: pageW - 20,
  })
}
